import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TokenConstants } from '../constants/MessageConstants';
import { CommonErrorMessages } from '../constants/ErrorMessages';
import { isPermissionDenied } from '../models/permission.model';
import { SharedService } from '../services/shared-service/shared.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private hasShownAuthError = false;
  private readonly csrfCookieName = 'collectx_admin_xsrf';
  private readonly csrfHeaderName = 'X-CSRF-TOKEN';

  constructor(
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly sharedService: SharedService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let authReq = req.clone({ withCredentials: true });
    const isUnsafeMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method.toUpperCase());
    const csrfToken = this.getCsrfToken();
    if (isUnsafeMethod && csrfToken) {
      authReq = authReq.clone({
        setHeaders: { [this.csrfHeaderName]: csrfToken }
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // File downloads receive the JSON error body as a Blob; read it before deciding.
        if (error.status === HttpStatusCode.Forbidden && error.error instanceof Blob) {
          return from(error.error.text()).pipe(
            switchMap((text) => this.handleError(new HttpErrorResponse({
              error: this.parseJson(text),
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url ?? undefined,
            })))
          );
        }

        return this.handleError(error);
      })
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    // Role lacks the menu/action permission: inform, but keep the session (not a CSRF/session 403).
    if (isPermissionDenied(error)) {
      this.toastr.error(error.error?.message || CommonErrorMessages.PermissionDenied);
      return throwError(() => error);
    }

    if (
      error.status === HttpStatusCode.Unauthorized ||
      error.status === HttpStatusCode.Forbidden
    ) {
      this.handleUnauthorized();
    }

    return throwError(() => error);
  }

  private handleUnauthorized(): void {
    if (!this.hasShownAuthError) {
      this.hasShownAuthError = true;
      this.toastr.error(TokenConstants.Session_Expired);
    }

    // Clears localStorage and the CSRF value, and notifies session state (permissions, real-time connection).
    this.sharedService.clearStorage();
    this.router.navigate(['/login']);
  }

  private parseJson(text: string): unknown {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  /** Prefer readable cookie; fall back to sessionStorage (cross-subdomain production). */
  private getCsrfToken(): string {
    return this.getCookieValue(this.csrfCookieName) || sessionStorage.getItem(this.csrfCookieName) || '';
  }

  private getCookieValue(name: string): string {
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }
}
